import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Modal } from 'reactstrap';
import { CustomTooltipStatus } from 'components/shared/CustomTooltip';
import ErrorComponent from 'components/shared/ErrorComponent';
import { Paginator } from 'components/shared/Paginator';
import { Loader } from 'components/shared/Loader';
import SliderComponent from 'components/Netlinking/Slider/NetlinkingSlider';
import { GenericAutoComplete } from 'components/Netlinking/AutoComplete/VueGlobalSelectorAutoComplete';
import Calendar from 'components/Netlinking/Calendar/CalendarPicker';
import { CAMPAIGN_STATUS_CONFIG } from 'utils/Constants';
import { formatDate } from 'utils/DateUtils';
import axiosPixLink from 'config/axiosPixLink';
import { UserSessionSelector } from 'store/UserSession';
import OrderTypeFilter from 'components/shared/Filter/OrderTypeFilter';
import { FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW, FILTER_CAMPAIGN_STATUS_LIST } from 'utils/Constants';
import OrderStatusFilter from 'components/shared/Filter/OrderTypeVerified';
import OrderDateRangeFilter from 'components/shared/Filter/OrderDateRangeFilter';
import './order.css';
import TypeFilterCheckBoxGroup from 'components/Netlinking/CheckBox/TypeFilterCheckBoxGroup';

const pageNbOptions = [10, 25, 50, 100];
function StatusBadge({ statut, t }) {
    if (!statut) return <span>-</span>;
    const cfg = CAMPAIGN_STATUS_CONFIG[statut.toLowerCase()];
    const color = cfg?.color || '#696969';
    const label = cfg?.label || statut;

    return (
        <CustomTooltipStatus title={t(label)} placement="top">
            <span
                style={{
                    display: 'inline-block',
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'default',
                }}
            />
        </CustomTooltipStatus>
    );
}


const COLUMNS = [
    { label: 'Nom de la campagne', field: 'nomCampagne', sortable: true, type: 'string' },
    { label: 'ID Devis', field: 'idDevis', sortable: true, type: 'string' },
    { label: 'ID Tâche', field: 'idTache', sortable: true, type: 'string' },
    { label: 'Account', field: 'account', sortable: true, type: 'string' },
    { label: 'Consultant', field: 'consultant', sortable: true, type: 'string' },
    { label: 'Type', field: 'type', sortable: true, type: 'string' },
    { label: 'Date début', field: 'dateDebut', sortable: true, type: 'date' },
    { label: 'Deadline', field: 'deadline', sortable: true, type: 'date' },
    { label: 'Statut', field: 'statut', sortable: true, type: 'string' },
    { label: 'Nb liens en ligne', field: 'nbLiensEnLigne', sortable: true, type: 'number' },
    { label: 'CA vendu', field: 'caVendu', sortable: true, type: 'money' },
    { label: 'Budget restant', field: 'budgetRestant', sortable: true, type: 'money' },
    { label: 'Jours restants', field: 'joursRestants', sortable: true, type: 'number' },
];

const formatMoney = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
};

const getTodayLabel = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

function mapStatus(campaignStatus) {
    if (!campaignStatus) return null;
    const map = {
        CAMPAIGN_BROUILLON: 'brouillon',
        CAMPAIGN_STARTED_TO_BE_SELECTED_BY_CONSULTANT: 'brouillon',
        CAMPAIGN_NOTIFIED_CONSULTANT_TO_BE_FILTERED: 'non attribuee',
        CAMPAIGN_ANCRAGE_TO_BE_PREPARED: 'non attribuee',
        CAMPAIGN_ANCRAGE_VALIDATED: 'en cours',
        CAMPAIGN_NOTIFIED_CLIENT_TO_BE_VALIDATED: 'en cours',
        CAMPAIGN_NOTIFIED_SUPER_ADMIN_TO_BE_APPROVED: 'en cours',
        CAMPAIGN_NOTIFIED_SUPER_ADMIN_SUPER_CONSULTANT_TO_BE_APPROVED: 'en cours',
        CAMPAIGN_EN_LIGNE: 'en ligne',
    };
    return map[campaignStatus] ?? 'non attribuee';
}
// Ajouter cette fonction en dehors du composant
const computeJoursRestants = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dl = new Date(deadline);
    dl.setHours(0, 0, 0, 0);
    return Math.ceil((dl - today) / (1000 * 60 * 60 * 24));
};

export default function SuperAdminVueGlobale() {
    const { t } = useTranslation();
    const { user } = useSelector(UserSessionSelector);
    const history = useHistory();

    const [campaigns, setCampaigns] = useState([]);
    const [isError, setIsError] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const fetchedRef = useRef(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage, setOrdersPerPage] = useState(10);
    const [currentOrders, setCurrentOrders] = useState([]);
    const [pageNumbers, setPageNumbers] = useState([]);

    const [sortField, setSortField] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [sortedCache, setSortedCache] = useState(null);

    const [titleSearch, setTitleSearch] = useState('');
    const [filterStatuts, setFilterStatuts] = useState([]);

    const [filterPeriodeFrom, setFilterPeriodeFrom] = useState(null);
    const [filterPeriodeTo, setFilterPeriodeTo] = useState(null);
    const [filterDeadlineFrom, setFilterDeadlineFrom] = useState(null);
    const [filterBudgetMin, setFilterBudgetMin] = useState('');
    const [filterBudgetMax, setFilterBudgetMax] = useState('');
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);

    const [budgetValues, setBudgetValues] = useState([0, 0]);
    const [minBudgetValue, setMinBudgetValue] = useState(0);
    const [maxBudgetValue, setMaxBudgetValue] = useState(0);
    const [filterCaMin, setFilterCaMin] = useState('');
    const [filterCaMax, setFilterCaMax] = useState('');
    const [caValues, setCaValues] = useState([0, 0]);
    const [minCaValue, setMinCaValue] = useState(0);
    const [maxCaValue, setMaxCaValue] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedType, setSelectedType] = useState(FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW[0]);

    const [anchorElStatus, setAnchorElStatus] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState({ ...FILTER_CAMPAIGN_STATUS_LIST[0], text: 'Tous les types' });

    const handleClick = (index, event) => setAnchorEl({ [index]: event.currentTarget });
    const handleClose = () => setAnchorEl(null);

    const handleClickStatus = (index, event) => setAnchorElStatus({ [index]: event.currentTarget });
    const handleCloseStatus = () => setAnchorElStatus(null);
    const handleSliderBudgetChange = (_e, newValue) => {
        setBudgetValues(newValue);
        setMinBudgetValue(newValue[0]);
        setMaxBudgetValue(newValue[1]);
        setFilterBudgetMin(String(newValue[0]));
        setFilterBudgetMax(String(newValue[1]));
    };
    const handleMinBudgetInputChange = (e) => {
        const v = e.target.value === '' ? '' : Number(e.target.value);
        setMinBudgetValue(v);
        setBudgetValues([v || 0, budgetValues[1]]);
        setFilterBudgetMin(e.target.value);
    };
    const handleMaxBudgetInputChange = (e) => {
        const v = e.target.value === '' ? '' : Number(e.target.value);
        setMaxBudgetValue(v);
        setBudgetValues([budgetValues[0], v || 0]);
        setFilterBudgetMax(e.target.value);
    };
    const handleSliderCaChange = (_e, newValue) => {
        setCaValues(newValue);
        setMinCaValue(newValue[0]);
        setMaxCaValue(newValue[1]);
        setFilterCaMin(String(newValue[0]));
        setFilterCaMax(String(newValue[1]));
    };
    const handleMinCaInputChange = (e) => {
        const v = e.target.value === '' ? '' : Number(e.target.value);
        setMinCaValue(v);
        setCaValues([v || 0, caValues[1]]);
        setFilterCaMin(e.target.value);
    };
    const handleMaxCaInputChange = (e) => {
        const v = e.target.value === '' ? '' : Number(e.target.value);
        setMaxCaValue(v);
        setCaValues([caValues[0], v || 0]);
        setFilterCaMax(e.target.value);
    };
    const toggleStatut = (key) => {
        setFilterStatuts((prev) => prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]);
        setCurrentPage(1);
    };
    const removeStatut = (key) => { setFilterStatuts((prev) => prev.filter((s) => s !== key)); setCurrentPage(1); };


    const [filterTypes, setFilterTypes] = useState([]);
    const toggleType = (key) => { setFilterTypes((prev) => prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]); setCurrentPage(1); };
    const removeType = (key) => { setFilterTypes((prev) => prev.filter((t) => t !== key)); setCurrentPage(1); };
    // ── nouveaux filtres ──────────────────────────────────────────────
    const [filterAccounts, setFilterAccounts] = useState([]);
    const [filterIdDevis, setFilterIdDevis] = useState([]);
    const [filterIdTaches, setFilterIdTaches] = useState([]);
    const [filterConsultants, setFilterConsultants] = useState([]);

    const toggleAccount = (key) => { setFilterAccounts((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]); setCurrentPage(1); };
    const removeAccount = (key) => { setFilterAccounts((p) => p.filter((x) => x !== key)); setCurrentPage(1); };
    const toggleIdDevis = (key) => { setFilterIdDevis((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]); setCurrentPage(1); };
    const removeIdDevis = (key) => { setFilterIdDevis((p) => p.filter((x) => x !== key)); setCurrentPage(1); };
    const toggleIdTache = (key) => { setFilterIdTaches((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]); setCurrentPage(1); };
    const removeIdTache = (key) => { setFilterIdTaches((p) => p.filter((x) => x !== key)); setCurrentPage(1); };
    const toggleConsultant = (key) => { setFilterConsultants((p) => p.includes(key) ? p.filter((x) => x !== key) : [...p, key]); setCurrentPage(1); };
    const removeConsultant = (key) => { setFilterConsultants((p) => p.filter((x) => x !== key)); setCurrentPage(1); };
    useEffect(() => {
        document.title = t('PixLink - Vue Globale');
        window.dispatchEvent(new Event('locationchange'));
    }, []);
    // Restaurer les filtres depuis sessionStorage au montage
    useEffect(() => {
        const saved = sessionStorage.getItem('vueGlobaleFilters');
        if (!saved) return;
        const f = JSON.parse(saved);
        if (f.filterStatuts) setFilterStatuts(f.filterStatuts);
        if (f.filterTypes) setFilterTypes(f.filterTypes);
        if (f.filterAccounts) setFilterAccounts(f.filterAccounts);
        if (f.filterIdDevis) setFilterIdDevis(f.filterIdDevis);
        if (f.filterIdTaches) setFilterIdTaches(f.filterIdTaches);
        if (f.filterConsultants) setFilterConsultants(f.filterConsultants);
        if (f.filterPeriodeFrom) setFilterPeriodeFrom(f.filterPeriodeFrom);
        if (f.filterPeriodeTo) setFilterPeriodeTo(f.filterPeriodeTo);
        if (f.filterBudgetMin !== undefined) setFilterBudgetMin(f.filterBudgetMin);
        if (f.filterBudgetMax !== undefined) setFilterBudgetMax(f.filterBudgetMax);
        if (f.filterCaMin !== undefined) setFilterCaMin(f.filterCaMin);
        if (f.filterCaMax !== undefined) setFilterCaMax(f.filterCaMax);
        if (f.titleSearch) setTitleSearch(f.titleSearch);
        if (f.currentPage) setCurrentPage(f.currentPage);
        if (f.ordersPerPage) setOrdersPerPage(f.ordersPerPage);
    }, []);

    // Sauvegarder les filtres dans sessionStorage à chaque changement
    useEffect(() => {
        sessionStorage.setItem('vueGlobaleFilters', JSON.stringify({
            filterStatuts, filterTypes, filterAccounts, filterIdDevis,
            filterIdTaches, filterConsultants, filterPeriodeFrom, filterPeriodeTo,
            filterBudgetMin, filterBudgetMax, filterCaMin, filterCaMax,
            titleSearch, currentPage, ordersPerPage,
        }));
    }, [filterStatuts, filterTypes, filterAccounts, filterIdDevis, filterIdTaches,
        filterConsultants, filterPeriodeFrom, filterPeriodeTo, filterBudgetMin,
        filterBudgetMax, filterCaMin, filterCaMax, titleSearch, currentPage, ordersPerPage]);
    const accountOptions = useMemo(() =>
        [...new Set(campaigns.map((c) => c.account).filter(Boolean))].sort().map((v) => ({ key: v, label: v })),
        [campaigns]
    );

    // Si un compte est sélectionné, on filtre les campagnes par ce compte
    const campaignsBySelectedAccounts = useMemo(() =>
        filterAccounts.length > 0
            ? campaigns.filter((c) => filterAccounts.includes(c.account))
            : campaigns,
        [campaigns, filterAccounts]
    );

    const idDevisOptions = useMemo(() =>
        [...new Set(campaignsBySelectedAccounts.map((c) => c.idDevis).filter(Boolean))].sort().map((v) => ({ key: v, label: v })),
        [campaignsBySelectedAccounts]
    );

    const campaignsBySelectedDevis = useMemo(() =>
        filterIdDevis.length > 0
            ? campaignsBySelectedAccounts.filter((c) => filterIdDevis.includes(c.idDevis))
            : campaignsBySelectedAccounts,
        [campaignsBySelectedAccounts, filterIdDevis]
    );

    const idTacheOptions = useMemo(() =>
        [...new Set(campaignsBySelectedDevis.map((c) => c.idTache).filter(Boolean))].sort().map((v) => ({ key: v, label: v })),
        [campaignsBySelectedDevis]
    );


    const consultantOptions = useMemo(() =>
        [...new Set(campaignsBySelectedAccounts.map((c) => c.consultant).filter(Boolean))].sort().map((v) => ({ key: v, label: v })),
        [campaignsBySelectedAccounts]
    );
    useEffect(() => {
        if (fetchedRef.current) return;
        fetchedRef.current = true;
        setIsLoading(true);
        setIsError(false);
        const mail = user && (user.mail || user.email || user.Mail || user.Email);
        axiosPixLink
            .post('/campaign/vue-globale', { email: mail })
            .then((res) => {
                const raw = Array.isArray(res.data) ? res.data : [];
                const mapped = raw.map((c) => {
                    const deadline = c.deadLine ?? c.deadline ?? null;
                    return {
                        ...c,
                        deadline,
                        consultant: c.nomConsultant ?? c.consultant ?? '',
                        nbLiensEnLigne: c.linksOnline ?? c.nbLiensEnLigne ?? 0,
                        statut: c.displayStatus
                            ? c.displayStatus.toLowerCase().replace(/_/g, ' ')
                            : mapStatus(c.campaignStatus),
                        joursRestants: computeJoursRestants(deadline),
                    };
                });
                setCampaigns(mapped);
                setIsLoading(false);
            })
            .catch(() => { setIsError(true); setIsLoading(false); });
    }, []);


    const filteredOrders = useMemo(() => {
        return campaigns.filter((c) => {
            if (titleSearch) {
                const q = titleSearch.toLowerCase();
                const hay = [c.nomCampagne, c.idDevis, c.idTache, c.consultant].join(' ').toLowerCase();
                if (!hay.includes(q)) return false;
            }
            if (filterStatuts.length > 0 && !filterStatuts.includes(c.statut?.toLowerCase())) return false;
            if (filterTypes.length > 0 && !filterTypes.includes((c.type === 'ultraPremium' ? 'ultrapremium' : c.type?.toLowerCase()) || 'standard')) return false;

            if (selectedType.value) {
                const normalizedType = c.type === 'ultraPremium' ? 'ultrapremium' : (c.type?.toLowerCase() || 'standard');
                if (normalizedType !== selectedType.value) return false;
            }
            if (selectedStatus.value && c.statut?.toLowerCase() !== selectedStatus.value) return false;

            if (filterPeriodeFrom || filterPeriodeTo) {
                if (!c.dateDebut && !c.deadline) return false;
                const debut = c.dateDebut ? new Date(c.dateDebut) : null;
                const deadline = c.deadline ? new Date(c.deadline) : null;
                if (filterPeriodeFrom) {
                    const from = new Date(filterPeriodeFrom);
                    // La campagne doit se terminer après (ou le même jour que) la date de début du filtre
                    if (!deadline || deadline < from) return false;
                }
                if (filterPeriodeTo) {
                    const to = new Date(filterPeriodeTo);
                    // La campagne doit avoir commencé avant (ou le même jour que) la date de fin du filtre
                    if (!debut || debut > to) return false;
                }
            }

            if (filterAccounts.length > 0 && !filterAccounts.includes(c.account)) return false;
            if (filterIdDevis.length > 0 && !filterIdDevis.includes(c.idDevis)) return false;
            if (filterIdTaches.length > 0 && !filterIdTaches.includes(c.idTache)) return false;
            if (filterConsultants.length > 0 && !filterConsultants.includes(c.consultant)) return false;
            // Deadline from filter
            if (filterDeadlineFrom) {
                if (!c.deadline) return false;
                if (new Date(c.deadline) < new Date(filterDeadlineFrom)) return false;
            }
            // CA vendu
            const ca = c.caVendu ?? 0;
            if (filterCaMin !== '' && ca < Number(filterCaMin)) return false;
            if (filterCaMax !== '' && ca > Number(filterCaMax)) return false;

            const budget = c.budgetRestant ?? 0;
            if (filterBudgetMin !== '' && budget < Number(filterBudgetMin)) return false;
            if (filterBudgetMax !== '' && budget > Number(filterBudgetMax)) return false;
            return true;
        });
    }, [campaigns, titleSearch, filterStatuts, filterTypes, filterAccounts, filterIdDevis, filterIdTaches, filterConsultants, selectedType, selectedStatus, filterPeriodeFrom, filterPeriodeTo, filterDeadlineFrom, filterBudgetMin, filterBudgetMax, filterCaMin, filterCaMax]);
    const handleSortingChange = useCallback((field) => {
        const direction = field === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
        const col = COLUMNS.find((c) => c.field === field);
        const sorted = [...filteredOrders].sort((a, b) => {
            let aVal = a[field], bVal = b[field];
            if (col?.type === 'date') {
                aVal = aVal ? new Date(aVal).getTime() : -Infinity;
                bVal = bVal ? new Date(bVal).getTime() : -Infinity;
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
            } else if (col?.type === 'number' || col?.type === 'money') {
                aVal = aVal ?? -Infinity; bVal = bVal ?? -Infinity;
                return direction === 'asc' ? aVal - bVal : bVal - aVal;
            } else {
                aVal = (aVal || '').toString().toLowerCase();
                bVal = (bVal || '').toString().toLowerCase();
                return direction === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
            }
        });
        setSortField(field);
        setSortOrder(direction);
        setSortedCache(sorted);
    }, [filteredOrders, sortField, sortOrder]);

    useEffect(() => { setSortedCache(null); }, [filteredOrders]);
    const effectiveOrders = sortedCache || filteredOrders;

    useEffect(() => { if (titleSearch) setCurrentPage(1); }, [titleSearch]);

    useEffect(() => {
        const last = currentPage * ordersPerPage;
        const first = last - ordersPerPage;
        setCurrentOrders(effectiveOrders.slice(first, last));
        setPageNumbers([...Array(Math.ceil(effectiveOrders.length / ordersPerPage)).keys()].map((i) => i + 1));
    }, [currentPage, effectiveOrders, ordersPerPage]);

    useEffect(() => {
        if (pageNumbers.length > 0 && pageNumbers[pageNumbers.length - 1] < currentPage)
            setCurrentPage(pageNumbers[pageNumbers.length - 1]);
    }, [pageNumbers]);
    // Quand le compte change, vider les filtres enfants qui ne sont plus valides
    useEffect(() => {
        if (filterAccounts.length === 0) return;
        const validDevis = new Set(campaignsBySelectedAccounts.map((c) => c.idDevis));
        const validTaches = new Set(campaignsBySelectedDevis.map((c) => c.idTache));
        const validConsultants = new Set(campaignsBySelectedAccounts.map((c) => c.consultant));

        setFilterIdDevis((prev) => prev.filter((v) => validDevis.has(v)));
        setFilterIdTaches((prev) => prev.filter((v) => validTaches.has(v)));
        setFilterConsultants((prev) => prev.filter((v) => validConsultants.has(v)));
    }, [filterAccounts]);

    const resetFilters = () => {
        setFilterStatuts([]);
        setFilterTypes([]);
        setFilterAccounts([]);
        setFilterIdDevis([]);
        setFilterIdTaches([]);
        setFilterConsultants([]);
        setFilterPeriodeFrom(null);
        setFilterPeriodeTo(null);
        setFilterDeadlineFrom(null);
        setFilterBudgetMin(''); setFilterBudgetMax('');
        setBudgetValues([0, 0]); setMinBudgetValue(0); setMaxBudgetValue(0);
        setFilterCaMin(''); setFilterCaMax('');
        setCaValues([0, 0]); setMinCaValue(0); setMaxCaValue(0);
        setTitleSearch('');
        setCurrentPage(1);
        sessionStorage.removeItem('vueGlobaleFilters');
    };
    const hasActiveFilters =
        filterStatuts.length > 0 ||
        filterTypes.length > 0 ||
        filterAccounts.length > 0 || filterIdDevis.length > 0 ||
        filterIdTaches.length > 0 || filterConsultants.length > 0 ||
        filterPeriodeFrom || filterPeriodeTo ||
        filterDeadlineFrom ||
        filterBudgetMin !== '' || filterBudgetMax !== '' ||
        filterCaMin !== '' || filterCaMax !== '';


    const exportVueGlobale = () => {
        const rows = effectiveOrders.map((c) => ({
            'Nom client': c.account || '',
            'Réf Devis': c.idDevis || '',
            'Nom Consultant': c.consultant || '',
            'ID Tâche': c.idTache || '',
            'Nom Tâche': c.nomCampagne || '',
            'CA Client': c.caVendu ?? '',
            'CA Dépensé': c.caDepense ?? '',
            'Montant Restant': c.budgetRestant ?? '',
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vue Globale');
        XLSX.writeFile(wb, `${getTodayLabel()} - Export vue globale.xlsx`);
    };

    const exportAvancement = () => {
        const rows = [];
        effectiveOrders.forEach((c) => {
            const spots = c.spots || c.netlinkingSelecteds || [];
            if (spots.length === 0) {
                rows.push({
                    'Nom client': c.account || '',
                    'Réf Devis': c.idDevis || '',
                    'Nom Consultant': c.consultant || '',
                    'ID Tâche': c.idTache || '',
                    'Nom Tâche': c.nomCampagne || '',
                    'Domaine': '',
                    'Lien du spot': '',
                    'Ancre': '',
                    'Page de destination': '',
                    'Montant Restant': c.budgetRestant ?? '',
                });
            } else {
                spots.forEach((spot) => {
                    rows.push({
                        'Nom client': c.account || '',
                        'Réf Devis': c.idDevis || '',
                        'Nom Consultant': c.consultant || '',
                        'ID Tâche': c.idTache || '',
                        'Nom Tâche': c.nomCampagne || '',
                        'Domaine': spot.domain || '',
                        'Lien du spot': spot.spotLink || '',
                        'Ancre': spot.ancreLink || '',
                        'Page de destination': spot.insertionLink || '',
                        'Montant Restant': c.budgetRestant ?? '',
                    });
                });
            }
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Avancement');
        XLSX.writeFile(wb, `${getTodayLabel()} - Export détail des spots.xlsx`);
    };


    const formatFilterDate = (isoString) => {
        if (!isoString) return '';
        const d = new Date(isoString);
        return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };

    return (
        <div className="content-center">
            <div className="box-info-header">
                <div className="b-child-1">
                    <h1>{t('Vue Globale des Campagnes')}</h1>
                    <div className="box-header-top-dm header-info">
                        <h1>{effectiveOrders.length} {t('Campagne(s) trouvée(s)')}</h1>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ── */}
            <div className="input-container-excel">
                <div className="input-group input-cs" style={{ flex: '1 1 0', minWidth: '160px' }}>
                    <input
                        type="text"
                        className="form-control"
                        placeholder={t('Rechercher (nom, devis, tâche, consultant...)')}
                        value={titleSearch}
                        onChange={(e) => { setTitleSearch(e.target.value); setCurrentPage(1); }}
                    />
                </div>

                <div className="d-flex align-items-center item-per-table" style={{ flexShrink: 0 }}>
                    <span className="me-2">{t('Afficher les résultats')}</span>
                    <div className="dropdown" style={{ width: '70px' }}>
                        <button className="btn dropdown-toggle dropdown-custom w-100" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                            {ordersPerPage}
                            <span><i className="bi bi-chevron-down" /></span>
                        </button>
                        <ul className="dropdown-menu">
                            {pageNbOptions.filter((nb) => nb !== ordersPerPage).map((item, idx) => (
                                <li key={idx} onClick={() => { setOrdersPerPage(item); setCurrentPage(1); }}>
                                    <button className="dropdown-item">{item}</button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* FIX 1: gap between filter icon and export buttons */}
                <div
                    className={hasActiveFilters ? 'bt-filter-circle-selected vertical-col-filter' : 'bt-filter-circle vertical-col-filter-not-selected'}
                    style={{ marginRight: '12px' }}
                    onClick={() => setFilterPopupOpen(true)}
                />

                <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="bt-submit-y" onClick={exportVueGlobale}>{t('Exporter vue globale')}</button>
                    <button className="bt-submit-y" onClick={exportAvancement}>{t('Exporter détail spots')}</button>
                </div>
            </div>

            {/* ── Active filter tags ── */}
            {hasActiveFilters && (
                <div className="bright-yellow-div">
                    {filterStatuts.length > 0 && (
                        <>
                            <b>{t('Statut')}:</b>{' '}
                            {filterStatuts.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>{t(CAMPAIGN_STATUS_CONFIG[key]?.label || key)}</span>
                                    <button onClick={() => removeStatut(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {filterTypes.length > 0 && (
                        <>
                            <b>{t('Type')}:</b>{' '}
                            {filterTypes.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>
                                        {t(key === 'ultrapremium' ? 'ultra premium' : key)}
                                    </span>
                                    <button onClick={() => removeType(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {filterAccounts.length > 0 && (
                        <>
                            <b>{t('Compte')}:</b>{' '}
                            {filterAccounts.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>{key}</span>
                                    <button onClick={() => removeAccount(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {filterIdDevis.length > 0 && (
                        <>
                            <b>{t('ID Devis')}:</b>{' '}
                            {filterIdDevis.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>{key}</span>
                                    <button onClick={() => removeIdDevis(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {filterIdTaches.length > 0 && (
                        <>
                            <b>{t('ID Tâche')}:</b>{' '}
                            {filterIdTaches.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>{key}</span>
                                    <button onClick={() => removeIdTache(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {filterConsultants.length > 0 && (
                        <>
                            <b>{t('Consultant')}:</b>{' '}
                            {filterConsultants.map((key) => (
                                <span key={key} className="theme-container click-pointer">
                                    <span onClick={() => setFilterPopupOpen(true)}>{key}</span>
                                    <button onClick={() => removeConsultant(key)}>x</button>
                                </span>
                            ))}
                        </>
                    )}
                    {(filterCaMin !== '' || filterCaMax !== '') && (
                        <>
                            <b>{t('CA vendu')}:</b>{' '}
                            <span className="theme-container click-pointer">
                                <span onClick={() => setFilterPopupOpen(true)}>
                                    {filterCaMin !== '' ? filterCaMin + '€' : '0€'}
                                    {' – '}
                                    {filterCaMax !== '' ? filterCaMax + '€' : ''}
                                </span>
                                <button onClick={() => {
                                    setFilterCaMin(''); setFilterCaMax('');
                                    setCaValues([0, 0]); setMinCaValue(0); setMaxCaValue(0);
                                    setCurrentPage(1);
                                }}>x</button>
                            </span>
                        </>
                    )}
                    {/* Date début tag */}
                    {(filterPeriodeFrom || filterPeriodeTo) && (
                        <>
                            <b>{t('Période')}:</b>{' '}
                            <span className="theme-container click-pointer">
                                <span onClick={() => setFilterPopupOpen(true)}>
                                    {filterPeriodeFrom ? formatFilterDate(filterPeriodeFrom) : ''}
                                    {' → '}
                                    {filterPeriodeTo ? formatFilterDate(filterPeriodeTo) : ''}
                                </span>
                                <button onClick={() => {
                                    setFilterPeriodeFrom(null);
                                    setFilterPeriodeTo(null);
                                    setCurrentPage(1);
                                }}>x</button>
                            </span>
                        </>
                    )}

                    {filterDeadlineFrom && (
                        <>
                            <b>{t('Deadline')}:</b>{' '}
                            <span className="theme-container click-pointer">
                                <span onClick={() => setFilterPopupOpen(true)}>≥ {formatFilterDate(filterDeadlineFrom)}</span>
                                <button onClick={() => { setFilterDeadlineFrom(null); setCurrentPage(1); }}>x</button>
                            </span>
                        </>
                    )}
                    {(filterBudgetMin !== '' || filterBudgetMax !== '') && (
                        <>
                            <b>{t('Budget')}:</b>{' '}
                            <span className="theme-container click-pointer">
                                <span onClick={() => setFilterPopupOpen(true)}>
                                    {filterBudgetMax !== '' ? (filterBudgetMin !== '' ? filterBudgetMin : '0') + '€ – ' + filterBudgetMax + '€' : filterBudgetMin + '€'}
                                </span>
                                <button onClick={() => { setFilterBudgetMin(''); setFilterBudgetMax(''); setBudgetValues([0, 0]); setMinBudgetValue(0); setMaxBudgetValue(0); setCurrentPage(1); }}>x</button>
                            </span>
                        </>
                    )}
                </div>
            )}

            {/* ── Filter Modal ── */}
            <Modal isOpen={filterPopupOpen} toggle={() => setFilterPopupOpen(false)} backdrop={true}>
                <div>
                    <div className="col-12 col-md-12 box-screen">
                        <div className="box-img-bg bg-3" />
                        <div className="box-wrapper">
                            <button type="button" onClick={() => setFilterPopupOpen(false)} className="btn-close btnClose" data-bs-dismiss="modal" aria-label="Close" />
                            <div className="header-title">{t('Paramétres de filtrage')}</div>

                            {/* Statut */}
                            <div className="row row-mb">
                                <div className="label-title">{t('Avancement')} :</div>
                                <div className="col-12 col-md-4">
                                    <GenericAutoComplete variant="statut" onValueChange={toggleStatut} highlightedOptions={filterStatuts} />
                                </div>
                                {filterStatuts.length > 0 && (
                                    <div className="theme-list-container-filter" style={{ marginBottom: '0' }}>
                                        {filterStatuts.map((key) => (
                                            <span key={key} className="theme-container">
                                                <span>{t(CAMPAIGN_STATUS_CONFIG[key]?.label || key)}</span>
                                                <button onClick={() => removeStatut(key)}>x</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Type */}
                            <div className="row row-mb">
                                <div className="label-title">{t('Type')} :</div>
                                <TypeFilterCheckBoxGroup
                                    selectedTypes={filterTypes}
                                    onToggle={toggleType}
                                />
                            </div>

                            {/* Période */}
                            <div className="row row-mb">
                                <div className="label-title">{t('Période')} :</div>
                                <div className="d-flex align-items-end" style={{ gap: '16px' }}>
                                    <div className="col-12 col-md-4">
                                        <div className="label-title" style={{ fontSize: '13px', marginBottom: '4px' }}>
                                            {t('Date début')}
                                        </div>
                                        <Calendar
                                            key={filterPeriodeFrom || 'periode-from'}
                                            value={filterPeriodeFrom}
                                            onValueChange={(iso) => {
                                                setFilterPeriodeFrom(iso || null);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>

                                    <div className="col-12 col-md-4">
                                        <div className="label-title" style={{ fontSize: '13px', marginBottom: '4px' }}>
                                            {t('Deadline')}
                                        </div>
                                        <Calendar
                                            key={filterPeriodeTo || 'periode-to'}
                                            value={filterPeriodeTo}
                                            onValueChange={(iso) => {
                                                setFilterPeriodeTo(iso || null);
                                                setCurrentPage(1);
                                            }}
                                        />
                                    </div>
                                </div>

                                {(filterPeriodeFrom || filterPeriodeTo) && (
                                    <div className="theme-list-container-filter" style={{ marginTop: '8px', marginBottom: 0 }}>
                                        <span className="theme-container">
                                            <span>
                                                {filterPeriodeFrom ? formatFilterDate(filterPeriodeFrom) : ''}
                                                {' → '}
                                                {filterPeriodeTo ? formatFilterDate(filterPeriodeTo) : ''}
                                            </span>
                                            <button onClick={() => {
                                                setFilterPeriodeFrom(null);
                                                setFilterPeriodeTo(null);
                                                setCurrentPage(1);
                                            }}>x</button>
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Compte client */}
                            <div className="row row-mb">
                                <div className="label-title">{t('Compte client')} :</div>
                                <div className="col-12 col-md-4">
                                    <GenericAutoComplete
                                        variant="account"
                                        dynamicOptions={accountOptions}
                                        onValueChange={toggleAccount}
                                        highlightedOptions={filterAccounts}
                                    />
                                </div>
                                {filterAccounts.length > 0 && (
                                    <div className="theme-list-container-filter" style={{ marginBottom: '0' }}>

                                        {filterAccounts.map((key) => (
                                            <span key={key} className="theme-container">
                                                <span>{key}</span>
                                                <button onClick={() => removeAccount(key)}>x</button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* ID Devis */}

                            {filterAccounts.length > 0 && (
                                <>
                                    <div className="row row-mb" style={{ gap: '0', alignItems: 'flex-start' }}>
                                        {/* ID Devis */}
                                        <div className="col-12 col-md-4">
                                            <div className="label-title">{t('ID Devis')} :</div>
                                            <GenericAutoComplete
                                                variant="idDevis"
                                                dynamicOptions={idDevisOptions}
                                                onValueChange={toggleIdDevis}
                                                highlightedOptions={filterIdDevis}
                                            />
                                            {filterIdDevis.length > 0 && (
                                                <div className="theme-list-container-filter" style={{ marginBottom: '0' }}>
                                                    {filterIdDevis.map((key) => (
                                                        <span key={key} className="theme-container">
                                                            <span>{key}</span>
                                                            <button onClick={() => removeIdDevis(key)}>x</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* ID Tâche */}
                                        <div className="col-12 col-md-4">
                                            <div className="label-title">{t('ID Tâche')} :</div>
                                            <GenericAutoComplete
                                                variant="idTache"
                                                dynamicOptions={idTacheOptions}
                                                onValueChange={toggleIdTache}
                                                highlightedOptions={filterIdTaches}
                                            />
                                            {filterIdTaches.length > 0 && (
                                                <div className="theme-list-container-filter" style={{ marginBottom: '0' }}>
                                                    {filterIdTaches.map((key) => (
                                                        <span key={key} className="theme-container">
                                                            <span>{key}</span>
                                                            <button onClick={() => removeIdTache(key)}>x</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* Consultant */}
                                        <div className="col-12 col-md-4">
                                            <div className="label-title">{t('Consultant')} :</div>
                                            <GenericAutoComplete
                                                variant="consultant"
                                                dynamicOptions={consultantOptions}
                                                onValueChange={toggleConsultant}
                                                highlightedOptions={filterConsultants}
                                            />
                                            {filterConsultants.length > 0 && (
                                                <div className="theme-list-container-filter" style={{ marginBottom: '0' }}>
                                                    {filterConsultants.map((key) => (
                                                        <span key={key} className="theme-container">
                                                            <span>{key}</span>
                                                            <button onClick={() => removeConsultant(key)}>x</button>
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                            {/* Deadline
                            <div className="row row-mb">
                                <div className="label-title">{t('Deadline')} :</div>
                                <div className="col-12 col-md-6">
                                    <Calendar
                                        key={filterDeadlineFrom || 'deadline-picker'}
                                        onValueChange={(iso) => {
                                            if (!iso) return;
                                            setFilterDeadlineFrom(iso);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                {filterDeadlineFrom && (
                                    <div className="theme-list-container-filter">
                                        <span className="theme-container">
                                            <span>≥ {formatFilterDate(filterDeadlineFrom)}</span>
                                            <button onClick={() => { setFilterDeadlineFrom(null); setCurrentPage(1); }}>x</button>
                                        </span>
                                    </div>
                                )}
                            </div> */}

                            {/* CA vendu + Budget restant */}
                            <div className="row row-mb">
                                <SliderComponent
                                    SliderName={t('CA vendu (€)')}
                                    values={caValues}
                                    handleSliderChange={handleSliderCaChange}
                                    minValue={minCaValue}
                                    maxValue={maxCaValue}
                                    handleMinInputChange={handleMinCaInputChange}
                                    handleMaxInputChange={handleMaxCaInputChange}
                                    SliderLabel={t('CA vendu')}
                                />
                                <SliderComponent
                                    SliderName={t('Budget restant (€)')}
                                    values={budgetValues}
                                    handleSliderChange={handleSliderBudgetChange}
                                    minValue={minBudgetValue}
                                    maxValue={maxBudgetValue}
                                    handleMinInputChange={handleMinBudgetInputChange}
                                    handleMaxInputChange={handleMaxBudgetInputChange}
                                    SliderLabel={t('Budget restant')}
                                />
                            </div>

                            <div className="box-button-bt">
                                <div style={{ margin: '20px' }}>
                                    <button className="bt-submit-y" onClick={() => { resetFilters(); setFilterPopupOpen(false); }}>{t('Réinitialiser')}</button>
                                </div>
                                <div>
                                    <button className="bt-submit-y" onClick={() => setFilterPopupOpen(false)}>{t('Appliquer')}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            <br />

            {/* ── Table ── */}
            {isLoading ? (
                <Loader />
            ) : isError ? (
                <ErrorComponent />
            ) : (
                <div className="box-wrapper">
                    <div className="table-responsive">
                        <div className="box-table-netlinking">
                            <table className="table-lists table-commandes">
                                <thead>
                                    <tr>
                                        {COLUMNS.map(({ label, field, sortable }) => (
                                            <th key={field} style={{ cursor: sortable ? 'pointer' : 'default' }} onClick={sortable ? () => handleSortingChange(field) : undefined}>
                                                {t(label)}
                                                {sortable && field !== 'type' && field !== 'statut' && (
                                                    <i style={{ marginLeft: '3px' }} className={field === sortField && sortOrder === 'asc' ? 'bi bi-chevron-up' : 'bi bi-chevron-down'} />
                                                )}
                                                {field === 'type' && (
                                                    <OrderTypeFilter
                                                        orderTypeList={FILTER_CAMPAIGN_TYPE_LIST_GLOBAL_VIEW}
                                                        selectedType={selectedType}
                                                        setSelectedType={(val) => { setSelectedType(val); setCurrentPage(1); }}
                                                        handleClick={handleClick}
                                                        handleClose={handleClose}
                                                        anchorEl={anchorEl}
                                                    />
                                                )}
                                                {field === 'statut' && (
                                                    <OrderStatusFilter
                                                        orderVerifiedList={FILTER_CAMPAIGN_STATUS_LIST}
                                                        selectedVerified={
                                                            selectedStatus === FILTER_CAMPAIGN_STATUS_LIST[0]
                                                                ? { ...selectedStatus, text: 'Tous les types' }
                                                                : selectedStatus
                                                        }
                                                        setSelectedVerified={(val) => { setSelectedStatus(val); setCurrentPage(1); }}
                                                        handleClick={handleClickStatus}
                                                        handleClose={handleCloseStatus}
                                                        anchorEl={anchorElStatus}
                                                    />
                                                )}
                                                {/*  
                                                {field === 'dateDebut' && (
                                                    <OrderDateRangeFilter
                                                        filterFrom={filterPeriodeFrom}
                                                        filterTo={filterPeriodeTo}
                                                        setFilterFrom={setFilterPeriodeFrom}
                                                        setFilterTo={setFilterPeriodeTo}
                                                        setCurrentPage={setCurrentPage}
                                                    />
                                                )} */}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentOrders.length === 0 ? (
                                        <tr>
                                            <td colSpan={COLUMNS.length} className="text-center py-4 text-muted">{t('Aucune campagne trouvée')}</td>
                                        </tr>
                                    ) : (
                                        currentOrders.map((c, idx) => (
                                            <tr key={c.id || idx}>
                                                <td
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const role = user?.role ?? user?.roles?.[0] ?? '';
                                                        const base = role.includes('SUPER_ADMIN') || role.includes('superAdmin')
                                                            ? '/SuperAdmin/DetailsCampagne/'
                                                            : role.includes('SUPER_CONSULTANT') || role.includes('superConsultant')
                                                                ? '/SuperConsultant/DetailsCampagne/'
                                                                : '/Client/DetailsCampagne/';
                                                        history.push(base + c.id);
                                                    }}
                                                >
                                                    <span className="campaign-title-text">{c.nomCampagne || '-'}</span>
                                                </td>
                                                <td>{c.idDevis || '-'}</td>
                                                <td>{c.idTache || '-'}</td>
                                                <td>{c.account || '-'}</td>
                                                <td>{c.consultant || '-'}</td>
                                                <td>{t(c.type ? (c.type === 'ultraPremium' ? 'ultra premium' : c.type) : 'standard')}</td>
                                                <td>{c.dateDebut ? formatDate(c.dateDebut) : '-'}</td>
                                                <td>{c.deadline ? formatDate(c.deadline) : '-'}</td>
                                                <td><StatusBadge statut={c.statut} t={t} /></td>
                                                <td>{c.nbLiensEnLigne ?? '-'}</td>
                                                <td>{formatMoney(c.caVendu)}</td>
                                                <td>{formatMoney(c.budgetRestant)}</td>
                                                <td ref={(el) => {
                                                    if (el && c.joursRestants != null && c.joursRestants < 0) {
                                                        el.style.setProperty('color', '#dc3545', 'important');

                                                    }
                                                }}>
                                                    {c.joursRestants ?? '-'}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            <Paginator
                isLoading={isLoading}
                listLength={effectiveOrders.length}
                currentPage={currentPage}
                pageNumbers={pageNumbers}
                nbElementsPerPage={ordersPerPage}
                goToPage={(page) => setCurrentPage(page)}
            />
        </div>
    );
}